import { createElement } from 'lucide';
import {
  Upload, FolderPlus, Pencil, Trash2, File, Download, Plus, Check, X,
  GripVertical, Bold, Italic, List, Link, Code, ChevronDown, ChevronLeft, House, User,
  Users, GraduationCap, AlignLeft, Calendar, ChartLine, Settings, Sun, Moon,
  Bell, Menu, Music, Mail, CircleAlert, Image, Film, Folder, Clock, Play,
  Wallet, TrendingUp, TrendingDown, UserPlus, Activity, Receipt, Copy, ChartColumn,
  Cookie, Award,
} from 'lucide';

/**
 * Shared icon helper — the single source of icons for the whole client.
 *
 * Icons come from the dependency-free `lucide` package (icon data is an
 * `IconNode` = array of `[tag, attrs]` tuples); we render them with lucide's own
 * `createElement`, so there are no hand-copied SVG path strings anywhere. The
 * result is an inline `<svg>` placed directly in the calling component's template
 * (and therefore its shadow root), so existing `svg`-scoped component styles keep
 * applying. Stroke colour is `currentColor`, so icons inherit text colour.
 *
 * Usage: `${icon('trash-2')}` in a Lit template, optional `icon('trash-2', 16)`
 * for a non-default pixel size (CSS `svg { width }` rules still win over this).
 *
 * To add an icon: import it from 'lucide' (PascalCase) and add a kebab-case entry
 * to REGISTRY. Keep names matching lucide's own kebab slugs where possible.
 */
const REGISTRY = {
  'upload': Upload,
  'folder-plus': FolderPlus,
  'pencil': Pencil,
  'trash-2': Trash2,
  'file': File,
  'download': Download,
  'plus': Plus,
  'check': Check,
  'x': X,
  'grip-vertical': GripVertical,
  'bold': Bold,
  'italic': Italic,
  'list': List,
  'link': Link,
  'code': Code,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'home': House,
  'user': User,
  'users': Users,
  'graduation-cap': GraduationCap,
  'align-left': AlignLeft,
  'calendar': Calendar,
  'chart-line': ChartLine,
  'chart-bar': ChartColumn,
  'settings': Settings,
  'sun': Sun,
  'moon': Moon,
  'bell': Bell,
  'menu': Menu,
  'music': Music,
  'mail': Mail,
  'circle-alert': CircleAlert,
  'image': Image,
  'film': Film,
  'folder': Folder,
  'clock': Clock,
  'play': Play,
  'wallet': Wallet,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'user-plus': UserPlus,
  'activity': Activity,
  'receipt': Receipt,
  'copy': Copy,
  'cookie': Cookie,
  'award': Award,
} satisfies Record<string, Parameters<typeof createElement>[0]>;

export type IconName = keyof typeof REGISTRY;

/** Build an inline lucide `<svg>` element for `name`, sized `size`px (default 18). */
export function icon(name: IconName, size = 18): SVGElement {
  return createElement(REGISTRY[name], { width: size, height: size });
}
